<script>
import auth from '@/services/auth';

export default {
  name: 'AppSettings',
  computed: {
    isAdmin() {
      return auth.isAdmin();
    },
    isPlatform() {
      return auth.hasPlatformAccess();
    },
    canMarketing() {
      return auth.canAccessMarketing();
    },
  },
};
</script>

<template>
  <div class="settings">
    <div class="breadcrumb">
      <router-link to="/clients" class="breadcrumb-item">
        <i class="fas fa-home"></i>
      </router-link>
      <span class="breadcrumb-separator">/</span>
      <span class="breadcrumb-item active">Account</span>
    </div>

    <div class="settings-container">
      <div class="settings-sidebar">
        <div class="settings-nav">
          <p v-if="!$route.meta.hideSettingsNav" class="nav-group-label">Your account</p>
          <router-link
            to="/settings/profile"
            class="nav-item"
            active-class="active"
          >
            <i class="fas fa-user"></i>
            Profile
          </router-link>
          <router-link
            to="/settings/security"
            class="nav-item"
            active-class="active"
          >
            <i class="fas fa-lock"></i>
            Security
          </router-link>
          <router-link
            to="/settings/agent-profile"
            class="nav-item"
            active-class="active"
          >
            <i class="fas fa-robot"></i>
            Agent Profile
          </router-link>

          <template v-if="isAdmin">
            <p class="nav-group-label">Organization</p>
            <router-link
              to="/settings/organization"
              class="nav-item"
              active-class="active"
            >
              <i class="fas fa-building"></i>
              Organization
            </router-link>
            <router-link
              to="/settings/users"
              class="nav-item"
              active-class="active"
            >
              <i class="fas fa-users-cog"></i>
              Users
            </router-link>
            <router-link
              to="/settings/branding"
              class="nav-item"
              active-class="active"
            >
              <i class="fas fa-palette"></i>
              Branding
            </router-link>
            <router-link
              to="/settings/email-branding"
              class="nav-item"
              active-class="active"
            >
              <i class="fas fa-envelope-open-text"></i>
              Email Branding
            </router-link>
            <router-link
              to="/settings/billing"
              class="nav-item"
              active-class="active"
            >
              <i class="fas fa-credit-card"></i>
              Billing
            </router-link>
            <router-link
              to="/settings/payments"
              class="nav-item"
              active-class="active"
            >
              <i class="fas fa-university"></i>
              Payments
            </router-link>
          </template>

          <template v-if="canMarketing">
            <p class="nav-group-label">Marketing</p>
            <router-link
              to="/settings/marketing"
              class="nav-item"
              active-class="active"
            >
              <i class="fas fa-bullhorn"></i>
              Marketing
            </router-link>
          </template>

          <template v-if="isPlatform">
            <p class="nav-group-label">Platform</p>
            <router-link
              to="/settings/platform"
              class="nav-item"
              active-class="active"
            >
              <i class="fas fa-cogs"></i>
              Platform tools
            </router-link>
            <router-link
              to="/api-integrations"
              class="nav-item"
              active-class="active"
            >
              <i class="fas fa-plug"></i>
              API Integrations
            </router-link>
          </template>

          <template v-if="isAdmin">
            <p class="nav-group-label">Internal</p>
            <router-link
              to="/invoicing"
              class="nav-item"
              active-class="active"
            >
              <i class="fas fa-receipt"></i>
              Invoices
            </router-link>
            <router-link
              v-if="isPlatform"
              to="/expenses"
              class="nav-item"
              active-class="active"
            >
              <i class="fas fa-file-invoice-dollar"></i>
              Business Expenses
            </router-link>
          </template>
        </div>
      </div>

      <div class="settings-content">
        <router-view />
      </div>
    </div>
  </div>
</template>

<style scoped>
.settings {
  padding: 20px;
}

.settings-container {
  display: flex;
  gap: 30px;
  margin-top: 20px;
  align-items: flex-start;
}

.settings-sidebar {
  width: 250px;
  flex-shrink: 0;
}

.settings-nav {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding-bottom: 0.5rem;
}

.nav-group-label {
  font-size: 0.68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin: 0.85rem 1.25rem 0.35rem;
  padding: 0;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  color: #6c757d;
  text-decoration: none;
  border-left: 3px solid transparent;
  transition: all 0.2s;
}

.nav-item:hover {
  background: #f8f9fa;
  color: #2c3e50;
}

.nav-item.active {
  background: #f8f9fa;
  color: var(--color-primary, #2c3e8c);
  border-left-color: var(--color-primary, #2c3e8c);
  font-weight: 600;
}

.nav-item i {
  width: 20px;
  text-align: center;
}

.settings-content {
  flex: 1;
  min-width: 0;
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;
}

.breadcrumb-item {
  color: #64748b;
  text-decoration: none;
}

.breadcrumb-item:hover {
  color: #2c3e8c;
}

.breadcrumb-item.active {
  color: #1e293b;
  font-weight: 600;
}

.breadcrumb-separator {
  color: #cbd5e1;
}

@media (max-width: 900px) {
  .settings-container {
    flex-direction: column;
  }

  .settings-sidebar {
    width: 100%;
  }
}
</style>
